import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AIAssistant.css';




const AIAssistant = ({ products, onProductRecommend }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! 👋 I\'m your AR Shopping Assistant powered by Google Gemini AI. I can help you find products, explain AR features, and give personalized recommendations. What are you looking for today?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate(); // ✅ ADDED

  // Get API key from environment variable
  const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);


  // ✅ ENHANCED - Advanced Product Search Function with multiple strategies
  const searchAndFilterProducts = (userQuery) => {
    const query = userQuery.toLowerCase().trim();

    let matchedProducts = [];

    // ✅ ADDED: Detect non-product queries that should use Gemini API
    const nonProductKeywords = [
      'how', 'what', 'why', 'when', 'where', 'who', 'which',
      'explain', 'tell me', 'can you', 'could you', 'would you',
      'do you', 'does', 'is', 'are', 'am', 'was', 'were',
      'blockchain', 'verification', 'verify', 'authentic',
      'cancel', 'order', 'return', 'refund', 'exchange',
      'shipping', 'delivery', 'payment', 'checkout',
      'help', 'support', 'contact', 'policy',
      'warranty', 'guarantee', 'track', 'status'
    ];

    // Check if query is a question or help request
    const startsWithQuestion = ['how', 'what', 'why', 'when', 'where', 'who', 'which', 'can', 'could', 'would', 'do', 'does', 'is', 'are'].some(q => query.startsWith(q));

    // If query is clearly a question or contains help keywords (and NOT a product search)
    if (startsWithQuestion || nonProductKeywords.some(keyword => query.includes(keyword))) {
      // Allow only if it's explicitly a product search with "show me" or "find"
      if (!query.includes('show') && !query.includes('find') && !query.includes('search') && !query.includes('get me')) {
        return []; // Return empty to trigger Gemini API
      }
    }

    // Strategy 1: Exact phrase matching for specific combinations
    const exactPhrases = {
      'office chair': (p) => p.name.toLowerCase().includes('office') && p.name.toLowerCase().includes('chair'),
      'washing machine': (p) => p.name.toLowerCase().includes('washing') || p.name.toLowerCase().includes('machine'),
      'laptop': (p) => p.name.toLowerCase().includes('laptop') || p.name.toLowerCase().includes('macbook') || p.name.toLowerCase().includes('computer'),
    };

    for (const [phrase, matcher] of Object.entries(exactPhrases)) {
      if (query.includes(phrase)) {
        matchedProducts = products.filter(matcher);
        if (matchedProducts.length > 0) return matchedProducts;
      }
    }

    // Strategy 2: Category-based search with synonyms
    const categoryMap = {
      'furniture': ['furniture', 'furnishing', 'home furniture'],
      'decor': ['decor', 'decoration', 'decorative', 'wall art', 'art'],
      'vehicle': ['vehicle', 'car', 'auto', 'automobile', 'transport'],
      'electronics': ['electronics', 'electronic', 'gadget', 'device', 'tech'],
      'lighting': ['lighting', 'light', 'lamp', 'illuminate'],
    };

    for (const [category, keywords] of Object.entries(categoryMap)) {
      if (keywords.some(keyword => query.includes(keyword))) {
        matchedProducts = products.filter(p =>
          p.category && p.category.toLowerCase() === category
        );
        if (matchedProducts.length > 0) return matchedProducts;
      }
    }

    // Strategy 3: Product-specific keywords with fuzzy matching
    const productKeywords = {
      'chair': ['chair', 'seating', 'seat'],
      'sofa': ['sofa', 'couch', 'settee'],
      'bed': ['bed', 'mattress', 'bedroom'],
      'table': ['table', 'desk', 'dining'],
      'pot': ['pot', 'planter', 'vase', 'plant'],
      'almirah': ['almirah', 'wardrobe', 'cupboard', 'closet', 'cabinet'],
      'car': ['car', 'vehicle', 'auto', 'automobile'],
      'laptop': ['laptop', 'macbook', 'notebook', 'computer'],
      'washing machine': ['washing', 'machine', 'washer', 'laundry'],
      'lamp': ['lamp', 'light', 'lighting'],
    };

    for (const [product, synonyms] of Object.entries(productKeywords)) {
      if (synonyms.some(syn => query.includes(syn))) {
        matchedProducts = products.filter(p =>
          synonyms.some(syn => p.name.toLowerCase().includes(syn)) ||
          p.name.toLowerCase().includes(product)
        );
        if (matchedProducts.length > 0) return matchedProducts;
      }
    }

    // Strategy 4: Word-by-word matching - ONLY for explicit product searches
    if (query.includes('show') || query.includes('find') || query.includes('search') || query.includes('get me')) {
      const queryWords = query.split(' ').filter(word => word.length > 3); // Increased to 4 letters minimum
      if (queryWords.length > 0) {
        matchedProducts = products.filter(p => {
          const productText = `${p.name} ${p.category || ''}`.toLowerCase(); // Removed description to avoid false matches
          return queryWords.some(word => productText.includes(word));
        });
        if (matchedProducts.length > 0) return matchedProducts;
      }
    }

    return matchedProducts;
  };


  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');
    setIsLoading(true);

    // ✅ NEW: Handle common questions WITHOUT API call (BEFORE API key check)
    const commonQuestions = {
      'what can i view in ar': 'All our products support AR viewing! 🎮 You can view:\n• Chairs & Sofas\n• Beds & Tables\n• Electronics (Laptops, Refrigerators)\n• Vehicles\n• Decorative Items\n• Art & Sculptures\n\nJust click "View in AR" on any product to place it in your real space!',
      'what can i view in ar?': 'All our products support AR viewing! 🎮 You can view:\n• Chairs & Sofas\n• Beds & Tables\n• Electronics (Laptops, Refrigerators)\n• Vehicles\n• Decorative Items\n• Art & Sculptures\n\nJust click "View in AR" on any product to place it in your real space!',
      'how does ar work': 'AR is easy! 📱\n\n1. Click "View in AR" on any product\n2. Point your phone camera at a flat surface\n3. Tap to place the product\n4. Walk around to see it from all angles\n5. Take photos to share!\n\nWorks on iOS 12+ and Android 8+',
      'how does ar work?': 'AR is easy! 📱\n\n1. Click "View in AR" on any product\n2. Point your phone camera at a flat surface\n3. Tap to place the product\n4. Walk around to see it from all angles\n5. Take photos to share!\n\nWorks on iOS 12+ and Android 8+',
      'how does ar viewing work': 'AR is easy! 📱\n\n1. Click "View in AR" on any product\n2. Point your phone camera at a flat surface\n3. Tap to place the product\n4. Walk around to see it from all angles\n5. Take photos to share!\n\nWorks on iOS 12+ and Android 8+',
      'how does ar viewing work?': 'AR is easy! 📱\n\n1. Click "View in AR" on any product\n2. Point your phone camera at a flat surface\n3. Tap to place the product\n4. Walk around to see it from all angles\n5. Take photos to share!\n\nWorks on iOS 12+ and Android 8+',
      'what is blockchain verification': '🔐 **Blockchain Verification ensures:**\n\n• Every product is 100% authentic\n• Transparent supply chain tracking\n• Secure & tamper-proof records\n• Verified seller ratings\n• Protection against counterfeits\n\nLook for the 🛡️ badge on products!',
      'what is blockchain verification?': '🔐 **Blockchain Verification ensures:**\n\n• Every product is 100% authentic\n• Transparent supply chain tracking\n• Secure & tamper-proof records\n• Verified seller ratings\n• Protection against counterfeits\n\nLook for the 🛡️ badge on products!',
      "what's blockchain verification": '🔐 **Blockchain Verification ensures:**\n\n• Every product is 100% authentic\n• Transparent supply chain tracking\n• Secure & tamper-proof records\n• Verified seller ratings\n• Protection against counterfeits\n\nLook for the 🛡️ badge on products!',
      "what's blockchain verification?": '🔐 **Blockchain Verification ensures:**\n\n• Every product is 100% authentic\n• Transparent supply chain tracking\n• Secure & tamper-proof records\n• Verified seller ratings\n• Protection against counterfeits\n\nLook for the 🛡️ badge on products!'
    };

    const normalizedInput = currentInput.toLowerCase().trim();
    if (commonQuestions[normalizedInput]) {
      const assistantMessage = {
        role: 'assistant',
        content: commonQuestions[normalizedInput]
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsLoading(false);
      return; // ✅ EXIT WITHOUT API CALL
    }

    try {
      const matchedProducts = searchAndFilterProducts(currentInput);

      if (matchedProducts.length > 0) {
        const productNames = matchedProducts.map(p => `${p.name} ($${p.price})`).join(', ');
        const quickResponse = `Great choice! 🎉 I found ${matchedProducts.length} product${matchedProducts.length > 1 ? 's' : ''} for you: ${productNames}. Check them out on the page! You can view each product in AR by clicking "View in AR" button.`;
        const assistantMessage = {
          role: 'assistant',
          content: quickResponse
        };
        setMessages(prev => [...prev, assistantMessage]);
        sessionStorage.setItem('aiSearchResults', JSON.stringify(matchedProducts));
        navigate('/');
        if (onProductRecommend) {
          onProductRecommend(matchedProducts[0]);
        }
        setIsLoading(false);
        return;
      }

      // ✅ FIXED: Check API key BEFORE making request
      if (!GEMINI_API_KEY) {
        const errorMessage = {
          role: 'assistant',
          content: '🔑 API key not configured! \n\nTo use AI-powered answers, \n\nMeanwhile, I can help you search for products! Try:\n• "Show me office chairs"\n• "furniture"\n• "electronics"'
        };
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
        return; // ✅ EXIT HERE
      }

      const productContext = products.map(p => `${p.name} (${p.category || 'General'}): ${p.description || 'Available in AR view'} - Price: $${p.price || '99.99'}`).join('\n');
      const systemPrompt = `You are an AR shopping assistant for AR-Bazaar.\n\nAvailable products:\n${productContext}\n\nYour role:\n- Help users find products\n- Explain AR features (view in 3D, place in real space)\n- Answer questions about products\n- Be friendly and concise (under 100 words)\n\nAR Features:\n- Click "View in AR" on any product\n- Point camera at flat surface\n- Tap to place product\n- Walk around to see from all angles`;

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash-preview:generateContent?key=${GEMINI_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: systemPrompt }, { text: `User question: ${currentInput}` }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 1000 }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to get response from Gemini');
      }

      const data = await response.json();

      if (!data || !data.candidates || data.candidates.length === 0) {
        const fallbackMessage = {
          role: 'assistant',
          content: "I'm here to help! You can ask me about:\n\n• Specific products (office chairs, sofas, laptops)\n• Product categories (furniture, decor, electronics)\n• AR features and how to use them\n• Product recommendations\n\nWhat would you like to know?"
        };
        setMessages(prev => [...prev, fallbackMessage]);
        setIsLoading(false);
        return;
      }

      const aiResponse = data.candidates[0]?.content?.parts?.[0]?.text || '';

      if (!aiResponse) {
        const fallbackMessage = {
          role: 'assistant',
          content: "I can help you find products! Try searching for:\n• Office chairs\n• Furniture\n• Electronics\n• Decor items\n\nWhat are you looking for?"
        };
        setMessages(prev => [...prev, fallbackMessage]);
        setIsLoading(false);
        return;
      }

      const assistantMessage = {
        role: 'assistant',
        content: aiResponse
      };
      setMessages(prev => [...prev, assistantMessage]);

      const mentionedProducts = products.filter(p => aiResponse.toLowerCase().includes(p.name.toLowerCase()));
      if (mentionedProducts.length > 0 && onProductRecommend) {
        onProductRecommend(mentionedProducts[0]);
      }

    } catch (error) {
      console.error('Error calling Gemini API:', error);
      let errorMessage = '';

      // ✅ FIXED: Better error detection
      if (error.message.includes('API_KEY_INVALID') || error.message.includes('invalid') || error.message.includes('API key')) {
        errorMessage = '🔑 **Invalid API key!**\n\nPlease check your Gemini API key:\n1. Go to https://aistudio.google.com/apikey\n2. Create or copy a valid API key\n3. Update your .env file\n4. Restart the app\n\nMeanwhile, try product searches like:\n• "Show me office chairs"\n• "furniture"';
      } else if (error.message.includes('quota') || error.message.includes('rate limit') || error.message.includes('429')) {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const istNow = new Date(now.getTime() + istOffset);
        const nextReset = new Date(istNow);
        nextReset.setHours(13, 30, 0, 0);
        if (istNow.getHours() > 13 || (istNow.getHours() === 13 && istNow.getMinutes() >= 30)) {
          nextReset.setDate(nextReset.getDate() + 1);
        }
        const hoursUntilReset = Math.ceil((nextReset - istNow) / (1000 * 60 * 60));
        const resetDate = nextReset.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', weekday: 'short' });
        errorMessage = `⏳ **Daily API quota reached** (250 requests/day).\n\n🕐 Resets at **1:30 PM IST ${resetDate}** (in ~${hoursUntilReset} hours).\n\nMeanwhile, I can still help you search for products! Try:\n• "Show me office chairs"\n• "furniture"\n• "electronics"\n• "car"`;
      } else {
        errorMessage = `I'm having trouble connecting right now. But I can still help you search for products! Try:\n\n• "Show me office chairs"\n• "furniture"\n• "electronics"\n• "decor"`;
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessage
      }]);
    } finally {
      setIsLoading(false);
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const suggestedQuestions = [
    "Show me office chairs",
    "What can I view in AR?",
    "Recommend furniture for small spaces",
    "How does AR viewing work?",
    "Show me luxury items",
    "What's blockchain verification?"
  ];

  const handleSuggestionClick = (question) => {
    setInput(question);
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };


  return (
    <>
      {/* Floating Chat Button */}
      <button
        className="ai-chat-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open AI Assistant"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          <circle cx="12" cy="11" r="1" fill="currentColor"></circle>
          <circle cx="8" cy="11" r="1" fill="currentColor"></circle>
          <circle cx="16" cy="11" r="1" fill="currentColor"></circle>
        </svg>
        {!isOpen && <span className="ai-badge">AI</span>}
      </button>


      {/* Chat Window */}
      {isOpen && (
        <div className="ai-chat-window">
          <div className="ai-chat-header">
            <div className="ai-header-content">
              <div className="ai-avatar">🤖</div>
              <div>
                <h3>AR Shopping Assistant</h3>
                <span className="ai-status">
                  {isLoading ? 'Typing...' : '🟢 Powered by Gemini AI'}
                </span>
              </div>
            </div>
            <button
              className="ai-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>


          {/* Messages Area */}
          <div className="ai-messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`ai-message ${msg.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="ai-message-avatar">🤖</div>
                )}
                <div className="ai-message-content">
                  {msg.content}
                </div>
                {msg.role === 'user' && (
                  <div className="ai-message-avatar ai-user-avatar">👤</div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="ai-message ai-message-assistant">
                <div className="ai-message-avatar">🤖</div>
                <div className="ai-message-content ai-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>


          {/* Suggested Questions */}
          {messages.length === 1 && !isLoading && (
            <div className="ai-suggestions">
              <p className="ai-suggestions-title">Try asking:</p>
              <div className="ai-suggestions-grid">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    className="ai-suggestion-chip"
                    onClick={() => handleSuggestionClick(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}


          {/* Input Area with Voice Assistant */}
          <div className="ai-input-area">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              className="ai-input"
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || isLoading}
              className="ai-send-btn"
              aria-label="Send message"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>

          {/* Footer */}
          <div className="ai-footer">
            <span className="gemini-badge">⚡ Powered by Google Gemini AI</span>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
